import React, { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../contexts/AuthContext';
import { useOrganization } from '../contexts/OrganizationContext';
import { isFlagOn } from '../utils/featureFlags';
import DemoFeatureGuide from './DemoFeatureGuide';
import logo from '../assets/logo.png';
import {
  LayoutDashboard,
  Users,
  Image,
  MapPin,
  Settings,
  LogOut,
  Menu,
  X,
  BarChart3,
  DollarSign,
  Package,
  Building2,
  ChevronDown,
  Palette,
  Download,
  Receipt,
  Ticket,
  KanbanSquare,
  ShieldCheck,
  ShieldAlert,
  MonitorPlay,
  FileText,
  Share2,
  Contact2,
  Sparkles,
  ChevronRight,
  Briefcase,
  HeartHandshake
} from 'lucide-react';

const Layout = ({ children }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [showOrgDropdown, setShowOrgDropdown] = useState(false);
  const { currentUser, signOut, userRole } = useAuth();
  const { 
    currentOrgId, 
    currentOrganization, 
    organizations, 
    isViewingAllOrgs,
    effectiveRole,
    switchOrganization,
    switching,
    canSwitchOrg 
  } = useOrganization();
  const location = useLocation();
  const navigate = useNavigate();

  // Close dropdown when clicking outside
  React.useEffect(() => {
    const handleClickOutside = (event) => {
      if (showOrgDropdown && !event.target.closest('.org-dropdown')) {
        setShowOrgDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showOrgDropdown]);

  const isSalesperson = userRole === 'salesperson';

  // Demo sandbox: the demo superadmin exists to drive venue-owner demos, so
  // SocialSnap-internal surfaces (company CRM, affiliate program admin) are
  // hidden from the nav there — they'd only ever show wiped-empty pages.
  const isDemoEnv = import.meta.env.VITE_FIREBASE_PROJECT_ID === 'socialsnap-demo';

  // Surface count of orphaned payments (charged but session never started or
  // never linked) so superadmins can spot them without trawling Payments.
  // Acknowledged orphans are excluded — superadmin clears them per-row from
  // the Payments page once reviewed/refunded out of band. Firestore can't
  // query "field absent," so the filter is applied client-side on the
  // snapshot. Volume of orphan docs should always be small (single digits in
  // steady state), so reading them all into memory is fine.
  const [orphanedPaymentCount, setOrphanedPaymentCount] = useState(0);
  useEffect(() => {
    if (userRole !== 'superadmin') {
      setOrphanedPaymentCount(0); // eslint-disable-line react-hooks/set-state-in-effect
      return;
    }
    const q = query(
      collection(db, 'stripe_payments'),
      where('status', 'in', ['orphaned_late_webhook', 'orphaned_duplicate'])
    );
    const unsub = onSnapshot(
      q,
      (snap) => {
        const unacknowledged = snap.docs.filter((d) => !d.data().acknowledged_at).length;
        setOrphanedPaymentCount(unacknowledged);
      },
      (err) => {
        console.error('Failed to subscribe to orphan count:', err);
        setOrphanedPaymentCount(0);
      }
    );
    return () => unsub();
  }, [userRole]);

  // Phase 3 feature flag: when on, the new consolidated Users & Access screen
  // appears for superadmins and org owners, replacing the four legacy entries
  // (Team, Org Managers, Sales Team, OrgOwners). Old screens stay routable
  // until phase 4 cuts them over.
  const usersAccessOn = isFlagOn('users-access') && (userRole === 'superadmin' || userRole === 'admin' || userRole === 'org_owner');

  // Sidebar is grouped into collapsible sections. Each section's items are
  // filtered by role/state before rendering — if zero items survive, the
  // section is skipped entirely.
  const isAdminOrSuper = userRole === 'superadmin' || userRole === 'admin';
  const isOwnerOrAdmin = userRole === 'org_owner' || isAdminOrSuper;

  const sections = [];

  // Dashboard — standalone, no section wrapper. Affiliates have no
  // dashboard (/ redirects them to /affiliates), so skip the entry.
  if (userRole !== 'affiliate') {
    sections.push({ standalone: true, name: 'Dashboard', href: '/', icon: LayoutDashboard });
  }

  // Customers
  {
    const items = [];
    if (isOwnerOrAdmin) {
      items.push({ name: 'Users', href: '/users', icon: Users });
      items.push({ name: 'Customers', href: '/customers', icon: Contact2 });
      items.push({ name: 'Sessions', href: '/sessions', icon: BarChart3 });
    }
    if (isAdminOrSuper) {
      items.push({
        name: 'Payments',
        href: '/payments',
        icon: DollarSign,
        badgeCount: orphanedPaymentCount,
        badgeTitle: 'Orphaned payments needing review'
      });
    }
    if (items.length) sections.push({ name: 'Customers', icon: Users, items });
  }

  // Organizations
  {
    const items = [];
    if (effectiveRole === 'superadmin' && isViewingAllOrgs) {
      items.push({ name: 'Organizations', href: '/organizations', icon: Building2 });
    }
    if (usersAccessOn) {
      items.push({ name: 'Users & Access', href: '/users-access', icon: ShieldCheck });
    }
    if (items.length) sections.push({ name: 'Organizations', icon: Building2, items });
  }

  // Booth Management
  {
    const items = [];
    if (isSalesperson || isOwnerOrAdmin) {
      items.push({ name: 'Booths', href: '/booths', icon: MapPin });
    }
    // Filters and Credit Packages are admin/superadmin tools — org owners no
    // longer get nav entries for them. The routes themselves are unchanged, so
    // any existing deep links still resolve for users allowed to load them.
    if (isAdminOrSuper) {
      items.push({ name: 'Filters', href: '/filters', icon: Image });
    }
    if (isOwnerOrAdmin) {
      items.push({ name: 'Themes', href: '/themes', icon: Palette });
      items.push({ name: 'Screen Themes', href: '/screen-themes', icon: MonitorPlay });
    }
    if (isAdminOrSuper) {
      items.push({ name: 'Credit Packages', href: '/packages', icon: Package });
    }
    if (items.length) sections.push({ name: 'Booth Management', icon: MapPin, items });
  }

  // CRM (superadmin + salesperson). Salespeople collaborate on the same
  // global pipeline — no data scoping. Automation rules tab is still
  // hidden from them inside Advanced Tools (system config, superadmin-only).
  if (!isDemoEnv && (userRole === 'superadmin' || userRole === 'salesperson')) {
    sections.push({
      name: 'CRM',
      icon: Briefcase,
      items: [
        { name: 'CRM Dashboard', href: '/crm', icon: LayoutDashboard, exact: true },
        { name: 'Contacts', href: '/crm/contacts', icon: Contact2, matchPrefix: true },
        { name: 'Tasks', href: '/crm/tasks', icon: KanbanSquare },
        { name: 'Advanced Tools', href: '/crm/tools', icon: Sparkles },
      ],
    });
  }

  // Affiliates (superadmin + affiliate). The page itself is tabbed —
  // Contacts / Pipeline / Documents / Info (+ Roster for superadmins).
  if (!isDemoEnv && (userRole === 'superadmin' || userRole === 'affiliate')) {
    sections.push({ standalone: true, name: 'Affiliates', href: '/affiliates', icon: HeartHandshake });
  }

  // Admin
  {
    const items = [];
    if (userRole === 'superadmin') {
      // Reports is internal financials — hidden in the demo env.
      if (!isDemoEnv) items.push({ name: 'Reports', href: '/reports', icon: FileText });
      items.push({ name: 'Discount Codes', href: '/discount-codes', icon: Ticket });
    }
    if (isAdminOrSuper) {
      items.push({ name: 'Payout Breakdown', href: '/payout-breakdown', icon: Receipt });
    }
    // Org owners get the same inbox, scoped to their org by
    // OrganizationContext. The page already branches on role: owners approve
    // posts, superadmins schedule them (calendar + Reel Studio stay hidden).
    if (isOwnerOrAdmin) {
      items.push({ name: 'Social Media', href: '/social-media', icon: Share2 });
    }
    // Versions (kiosk binaries/OTA) is internal ops — hidden in the demo env.
    if (!isDemoEnv && userRole === 'superadmin' && isViewingAllOrgs) {
      items.push({ name: 'Versions', href: '/versions', icon: Download });
    }
    // Privacy Requests and Settings are internal ops — hidden in the demo env.
    if (!isDemoEnv && userRole === 'superadmin') {
      items.push({ name: 'Privacy Requests', href: '/privacy-requests', icon: ShieldAlert });
      items.push({ name: 'Settings', href: '/settings', icon: Settings });
    }
    if (items.length) sections.push({ name: 'Admin', icon: Settings, items });
  }

  // Collapse state — persisted to localStorage by section name. Sections start
  // expanded by default and auto-expand if the current route is inside.
  const [collapsedSections, setCollapsedSections] = useState(() => {
    try {
      const raw = window.localStorage?.getItem('admin-nav-collapsed');
      if (raw) return new Set(JSON.parse(raw));
    } catch (_) { /* ignore */ }
    return new Set();
  });

  const persistCollapsed = (next) => {
    try {
      window.localStorage?.setItem('admin-nav-collapsed', JSON.stringify(Array.from(next)));
    } catch (_) { /* ignore */ }
  };

  const toggleSection = (name) => {
    setCollapsedSections((prev) => {
      const next = new Set(prev);
      if (next.has(name)) next.delete(name); else next.add(name);
      persistCollapsed(next);
      return next;
    });
  };

  const isItemActive = (item) => item.matchPrefix
    ? isActive(item.href, { matchPrefix: true })
    : isActive(item.href);

  const sectionHasActive = (section) =>
    section.items?.some((it) => isItemActive(it));

  const handleSignOut = async () => {
    try {
      await signOut();
      navigate('/login');
    } catch (error) {
      console.error('Error signing out:', error);
    }
  };

  const isActive = (path, opts) => {
    if (opts?.matchPrefix) return location.pathname === path || location.pathname.startsWith(`${path}/`);
    return location.pathname === path;
  };

  const handleOrgSwitch = (orgId) => {
    switchOrganization(orgId);
    setShowOrgDropdown(false);
  };

  const getOrgDisplayName = () => {
    if (isViewingAllOrgs) {
      return 'All Organizations';
    }
    return currentOrganization?.name || 'Select Organization';
  };

  const isStaging = import.meta.env.VITE_FIREBASE_PROJECT_ID === 'socialsnap-testing';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Staging Environment Banner */}
      {isStaging && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-amber-500 text-black text-center py-1 text-xs font-bold tracking-wide">
          STAGING ENVIRONMENT — socialsnap-testing
        </div>
      )}
      {/* Demo Environment Banner — the affiliate demo sandbox. Every number
          on screen is simulated; the banner keeps that unambiguous even in
          a screen-share. */}
      {isDemoEnv && (
        <div className="fixed top-0 left-0 right-0 z-[100] bg-primary-600 text-white text-center py-1 text-xs font-bold tracking-wide">
          DEMO ENVIRONMENT — all data is simulated
        </div>
      )}

      {/* Organization Switching Overlay */}
      {switching && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center">
          <div className="bg-white rounded-lg p-6 flex flex-col items-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600 mb-4"></div>
            <p className="text-gray-700 font-medium">Switching organization...</p>
          </div>
        </div>
      )}

      {/* Mobile sidebar backdrop */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-20 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <div
        className={`fixed inset-y-0 left-0 z-30 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 ${
          sidebarOpen ? 'translate-x-0' : '-translate-x-full'
        } ${isStaging ? 'pt-6' : ''}`}
      >
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="flex items-center justify-between h-16 px-6 border-b border-gray-200">
            <img src={logo} alt="SocialSnap" className="h-8 w-auto" />
            <button
              onClick={() => setSidebarOpen(false)}
              className="lg:hidden text-gray-500 hover:text-gray-700"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          {/* Organization Switcher */}
          {canSwitchOrg && (
            <div className="px-4 pt-4 pb-2 border-b border-gray-200">
              <div className="relative org-dropdown">
                <button
                  onClick={() => setShowOrgDropdown(!showOrgDropdown)}
                  className="w-full flex items-center justify-between px-3 py-2 text-sm font-medium text-gray-700 bg-gray-50 rounded-lg hover:bg-gray-100 transition"
                >
                  <div className="flex items-center min-w-0">
                    <Building2 className="w-4 h-4 mr-2 flex-shrink-0 text-gray-400" />
                    <span className="truncate">{getOrgDisplayName()}</span>
                  </div>
                  <ChevronDown className="w-4 h-4 ml-2 flex-shrink-0 text-gray-400" />
                </button>
                
                {showOrgDropdown && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 max-h-64 overflow-y-auto">
                    {userRole === 'superadmin' && (
                      <button
                        onClick={() => handleOrgSwitch(null)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                          isViewingAllOrgs ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                        }`}
                      >
                        All Organizations
                      </button>
                    )}
                    {organizations.map((org) => (
                      <button
                        key={org.id}
                        onClick={() => handleOrgSwitch(org.id)}
                        className={`w-full text-left px-3 py-2 text-sm hover:bg-gray-50 ${
                          currentOrgId === org.id ? 'bg-blue-50 text-blue-700 font-medium' : 'text-gray-700'
                        }`}
                      >
                        {org.name}
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Organization Display (for single-org owners only) */}
          {!canSwitchOrg && currentOrganization && (
            <div className="px-4 pt-4 pb-2 border-b border-gray-200">
              <div className="flex items-center px-3 py-2 bg-blue-50 rounded-lg">
                <Building2 className="w-4 h-4 mr-2 text-blue-600" />
                <span className="text-sm font-medium text-blue-700 truncate">
                  {currentOrganization.name}
                </span>
              </div>
            </div>
          )}

          {/* Navigation — grouped into collapsible sections */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {sections.map((section) => {
              // Standalone items render as a normal link without a section wrapper.
              if (section.standalone) {
                const Icon = section.icon;
                const active = isItemActive(section);
                return (
                  <Link
                    key={section.href}
                    to={section.href}
                    onClick={() => setSidebarOpen(false)}
                    className={`flex items-center px-4 py-3 text-sm font-medium rounded-lg transition ${
                      active ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-100'
                    }`}
                  >
                    <Icon className={`mr-3 h-5 w-5 ${active ? 'text-primary-600' : 'text-gray-400'}`} />
                    {section.name}
                  </Link>
                );
              }

              const SectionIcon = section.icon;
              const hasActive = sectionHasActive(section);
              // If the current route is inside this section, force-expand it
              // — never hide the user's current location.
              const collapsed = !hasActive && collapsedSections.has(section.name);

              return (
                <div key={section.name} className="pt-3 first:pt-0">
                  <button
                    type="button"
                    onClick={() => toggleSection(section.name)}
                    className="w-full flex items-center justify-between px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wider text-gray-400 hover:text-gray-600 transition"
                  >
                    <span className="flex items-center gap-2">
                      {SectionIcon && <SectionIcon className="w-3.5 h-3.5" />}
                      {section.name}
                    </span>
                    <ChevronRight
                      className={`w-3.5 h-3.5 transition-transform ${collapsed ? '' : 'rotate-90'}`}
                    />
                  </button>
                  {!collapsed && (
                    <div className="mt-1 space-y-1">
                      {section.items.map((item) => {
                        const Icon = item.icon;
                        const active = isItemActive(item);
                        return (
                          <Link
                            key={item.href}
                            to={item.href}
                            onClick={() => setSidebarOpen(false)}
                            className={`flex items-center px-4 py-2 text-sm font-medium rounded-lg transition ${
                              active ? 'bg-primary-50 text-primary-700' : 'text-gray-700 hover:bg-gray-100'
                            }`}
                          >
                            <Icon className={`mr-3 h-5 w-5 ${active ? 'text-primary-600' : 'text-gray-400'}`} />
                            <span className="flex-1">{item.name}</span>
                            {item.badgeCount > 0 && (
                              <span
                                title={item.badgeTitle}
                                className="ml-2 inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 text-[10px] font-semibold rounded-full bg-red-600 text-white"
                              >
                                {item.badgeCount}
                              </span>
                            )}
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </nav>

          {/* User section */}
          <div className="border-t border-gray-200 p-4">
            <div className="flex items-center mb-3">
              <div className="flex-shrink-0 h-10 w-10 rounded-full bg-primary-100 flex items-center justify-center">
                <span className="text-primary-600 font-medium text-sm">
                  {currentUser?.email?.charAt(0).toUpperCase()}
                </span>
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium text-gray-700 truncate">
                  {currentUser?.email}
                </p>
                <p className="text-xs text-gray-500">
                  {userRole === 'superadmin' ? 'Super Admin' : userRole === 'org_owner' ? 'Organization Owner' : userRole === 'salesperson' ? 'Sales Representative' : userRole === 'admin' ? 'Administrator' : userRole === 'affiliate' ? 'Affiliate' : 'Organization Manager'}
                </p>
              </div>
            </div>
            <button
              onClick={handleSignOut}
              className="w-full flex items-center px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 rounded-lg transition"
            >
              <LogOut className="mr-3 h-5 w-5" />
              Sign out
            </button>
          </div>
        </div>
      </div>

      {/* Main content */}
      <div className="lg:pl-64">
        {/* Top bar — mobile only (hamburger). Desktop hides it entirely. */}
        <div className="sticky top-0 z-10 bg-white shadow-sm lg:hidden">
          <div className="flex items-center h-16 px-4 sm:px-6">
            <button
              onClick={() => setSidebarOpen(true)}
              className="text-gray-500 hover:text-gray-700"
            >
              <Menu className="h-6 w-6" />
            </button>
          </div>
        </div>

        {/* Page content */}
        <main className="p-4 sm:p-6 lg:p-8">
          <DemoFeatureGuide />
          {children}
        </main>
      </div>
    </div>
  );
};

export default Layout;
